/**
 * Module summary:
 * Shared input hardening helpers for the cleaned application. These utilities
 * normalize user-entered strings, enforce lightweight format rules, and cap
 * freeform payload sizes before the app persists them to Firebase or local
 * state.
 */

import type { ResponseData } from '../types/assessment';

const CONTROL_CHARS_REGEX = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;
const MULTI_SPACE_REGEX = /[ \t]{2,}/g;
const MULTI_NEWLINE_REGEX = /\n{3,}/g;
const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INPUT_LIMITS = {
  displayName: 80,
  email: 254,
  organization: 120,
  address: 180,
  city: 80,
  assessmentName: 120,
  assessmentAddress: 180,
  profileNote: 180,
  questionText: 2000,
  questionComment: 4000,
  genericShortText: 160,
} as const;

function stripControlChars(value: string): string {
  // Remove non-printable characters so stored text cannot carry hidden bytes into reports or logs.
  return value.replace(CONTROL_CHARS_REGEX, '');
}

function normalizeWhitespace(value: string): string {
  return stripControlChars(value)
    .replace(/\r\n?/g, '\n')
    .replace(MULTI_SPACE_REGEX, ' ')
    .replace(MULTI_NEWLINE_REGEX, '\n\n')
    .trim();
}

function normalizeDraftWhitespace(value: string): string {
  return stripControlChars(value)
    .replace(/\r\n?/g, '\n')
    .replace(MULTI_SPACE_REGEX, ' ')
    .replace(MULTI_NEWLINE_REGEX, '\n\n');
}

export function sanitizeSingleLineText(value: string, maxLength: number): string {
  // Collapse internal whitespace to keep names, addresses, and labels consistent across UI and exports.
  return normalizeWhitespace(value).replace(/\s+/g, ' ').slice(0, maxLength);
}

export function sanitizeMultilineText(value: string, maxLength: number): string {
  // Preserve intentional paragraph breaks while still trimming excessive whitespace and hard length limits.
  return normalizeWhitespace(value).slice(0, maxLength);
}

export function sanitizeMultilineDraft(value: string, maxLength: number): string {
  // Keep in-progress leading/trailing spaces while typing so controlled textareas do not collapse words together.
  return normalizeDraftWhitespace(value).slice(0, maxLength);
}

export function toOptionalSingleLineText(value: string | undefined, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const sanitized = sanitizeSingleLineText(value, maxLength);
  return sanitized || undefined;
}

export function normalizeEmail(value: string): string {
  return sanitizeSingleLineText(value, INPUT_LIMITS.email).toLowerCase();
}

export function isValidEmail(value: string): boolean {
  return SIMPLE_EMAIL_REGEX.test(normalizeEmail(value));
}

export function normalizeUsPhoneDigits(value: string): string {
  // Keep only digits so display formatting does not leak into backend validation or persistence.
  return value.replace(/\D/g, '');
}

export function normalizeUsPhoneE164(value: string): string | undefined {
  const digits = normalizeUsPhoneDigits(value);
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return undefined;
}

export function isStrongPassword(value: string): boolean {
  return value.length >= 8 && /[A-Z]/.test(value) && /[0-9]/.test(value);
}

export function sanitizeAssessmentResponses(responses: ResponseData): ResponseData {
  return Object.fromEntries(
    Object.entries(responses).map(([key, value]) => {
      if (typeof value === 'string') {
        const maxLength = key.endsWith('Comment') ? INPUT_LIMITS.questionComment : INPUT_LIMITS.questionText;
        return [key, sanitizeMultilineText(value, maxLength)];
      }
      if (Array.isArray(value)) {
        // Normalize every multi-select string so reports and filters do not receive inconsistent option text.
        return [key, value.map((entry) => sanitizeSingleLineText(String(entry), INPUT_LIMITS.genericShortText))];
      }
      return [key, value];
    })
  );
}

export function assert(condition: boolean, message: string): void {
  if (!condition) {
    throw new Error(message);
  }
}
